import React from 'react';
import {
  Order,
  core_apimessages_Country as Country,
} from '@reverbdotcom/commons/src/gql/graphql';
import { CountrySelector } from '../country_selector';
import bind from '@reverbdotcom/commons/src/bind';
import I18n from 'i18n-js';
import { ICustomsInfo } from './customs_info_utils';
import { IMoney, ICountry } from '../../../schemas/core-schema';
import { decamelizeKeys } from 'humps';

import ControlledInput from '@reverbdotcom/commons/src/components/controlled_input';

interface IProps {
  order: Order;
  customsInfo: ICustomsInfo;
  updateDeclaredValue: Function;
  updateDescription: Function;
  updateCountryOfOrigin: Function;
  countries: Country[];
  isOverCustomsLimit: boolean;
  customsLimitWarningText: string;
}

export default class OrderCustomsInfo extends React.Component<IProps, null> {
  decamelizedCountries() {
    return this.props.countries.map(c => decamelizeKeys(c)) as ICountry[];
  }

  @bind
  updateDeclaredValue(amountAttribute: Partial<IMoney>) {
    const newDeclaredValue = {

      ...this.props.customsInfo.declaredValue,
      ...amountAttribute,
    };

    return this.props.updateDeclaredValue(this.props.order, newDeclaredValue);
  }

  @bind
  updateDescription(event) {
    return this.props.updateDescription(this.props.order, event.target.value);
  }

  @bind
  updateCountryCode(countryCode) {
    return this.props.updateCountryOfOrigin(this.props.order, countryCode);
  }

  renderCustomsLimitWarning() {
    if (!this.props.isOverCustomsLimit) { return null; }

    return (
      <div className="color-red weight-bold mt-1 size-80">
        {this.props.customsLimitWarningText}
      </div>
    );
  }

  renderCustomsInfo() {
    return (
      <table className="table topaligned unbordered medium-rows">
        <tbody>
          <tr>
            <td className="pr-2">
              <label className="weight-bold size-90">
                {I18n.t('discovery.dashboard.selling.shippingLabels.orderCustoms.quantity')}
              </label>
            </td>
            <td>
              <span id={`${this.props.order.id}-quantity`}>
                {this.props.order.quantity}
              </span>
            </td>
          </tr>

          <tr>
            <td className="pr-2">
              <label className="weight-bold size-90">
                {I18n.t('discovery.dashboard.selling.shippingLabels.orderCustoms.tariffNumber')}
              </label>
            </td>
            <td>
              <span id={`${this.props.order.id}-tarriffNumber`}>
                {this.props.customsInfo.tariffNumber}
              </span>
            </td>
          </tr>

          <tr>
            <td className="pr-2">
              <label className="weight-bold size-90">
                {I18n.t('discovery.dashboard.selling.shippingLabels.orderCustoms.contentType')}
              </label>
            </td>
            <td>
              {I18n.t('discovery.dashboard.selling.shippingLabels.orderCustoms.merchandise')}
            </td>
          </tr>

          <tr>
            <td className="pr-2">
              <label className="weight-bold size-90">
                {I18n.t('discovery.dashboard.selling.shippingLabels.orderCustoms.restrictionType')}
              </label>
            </td>
            <td>
              {I18n.t('discovery.dashboard.selling.shippingLabels.orderCustoms.none')}
            </td>
          </tr>

          <tr>
            <td className="pr-2">
              <label className="weight-bold size-90">
                {I18n.t('discovery.dashboard.selling.shippingLabels.orderCustoms.nonDeliveryOption')}
              </label>
            </td>
            <td>
              {I18n.t('discovery.dashboard.selling.shippingLabels.orderCustoms.returnToSender')}
              <p className="form-group__help-text mb-0">
                {I18n.t('discovery.dashboard.selling.shippingLabels.orderCustoms.nonDeliveryTooltipHtml')}
              </p>
            </td>
          </tr>
        </tbody>
      </table>
    );
  }

  renderCustomsForm() {
    return (
      <div>
        <div className="form-group">
          <div className="form-group__header">
            <label htmlFor={`customs_order_${this.props.order.id}_description`}>
              {I18n.t('discovery.dashboard.selling.shippingLabels.orderCustoms.description')}
            </label>
          </div>
          <div className="form-group__fields">
            <textarea
              className="text"
              required
              id={`customs_order_${this.props.order.id}_description`}
              name={`customs_order_${this.props.order.id}_description`}
              value={this.props.customsInfo.description}
              onChange={this.updateDescription}
            >
            </textarea>
          </div>
        </div>

        <div className="form-group">
          <div className="form-group__header">
            <label htmlFor={`customs_order_${this.props.order.id}_amount`}>
              {I18n.t('discovery.dashboard.selling.shippingLabels.orderCustoms.declaredValue')}
            </label>
          </div>
          <div className="form-group__fields">
            <div className="input-group">
              <div className="input-group__text">
                {this.props.order.settlementAmountProductSubtotal.symbol}
              </div>
              <ControlledInput
                numberOnly
                className="input-group__input"
                fieldName="amount"
                inputName={`customs_order_${this.props.order.id}_amount`}
                inputValue={this.props.customsInfo.declaredValue.amount}
                required
                updateField={this.updateDeclaredValue}
              />
            </div>
            {this.renderCustomsLimitWarning()}
          </div>
        </div>

        <div className="form-group mb-0">
          <div className="form-group__header">
            <label htmlFor={`customs_order_${this.props.order.id}_country_of_origin`}>
              {I18n.t('discovery.dashboard.selling.shippingLabels.orderCustoms.countryOfOrigin')}
              <span className="form-group__help-text mtb-0 ml-1">
                {I18n.t('discovery.dashboard.selling.shippingLabels.orderCustoms.whereItWasMade')}
              </span>
            </label>
          </div>
          <div className="form-group__fields">
            <div className="mb-0">
              <CountrySelector
                inputName={`customs_order_${this.props.order.id}_country_of_origin`}
                countries={this.decamelizedCountries()}
                onChange={this.updateCountryCode}
                value={this.props.customsInfo.countryOfOrigin}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  render() {
    return (
      <div>
        <hr className="cms-hr" />
        <h4 className="heading-4 scaling-mb-2">
          {I18n.t(
            'discovery.dashboard.selling.shippingLabels.orderCustoms.customsForOrder',
            { orderId: this.props.order.legacyOrderId },
          )}
        </h4>
        <div className="g-container">
          <div className="g-col-6 g-col-mobile-12">
            {this.renderCustomsInfo()}
          </div>
          <div className="g-col-6 g-col-mobile-12 mobile-mt-1">
            {this.renderCustomsForm()}
          </div>
        </div>
      </div>
    );
  }
}
